import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Eye, EyeOff, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, isRTL } = useLanguage();
  
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    full_name: "",
    phone: "",
    email: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t("passwordsNoMatch"));
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error(t("passwordTooShort"));
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { confirmPassword, ...registerData } = formData;
      await register({ ...registerData, role: "customer" });
      toast.success(t("accountCreated"));
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("registrationFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link
          to="/"
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowIcon className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
          {t("backToHome")}
        </Link>
        
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-0">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#1a365d] rounded-full flex items-center justify-center">
              <span className="text-white font-heading font-bold text-2xl">بر</span>
            </div>
            <CardTitle className="font-heading text-2xl">{t("createAccount")}</CardTitle>
            <CardDescription>{t("registerWithUs")}</CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div>
                <Label htmlFor="full_name" className="mb-2 block">{t("fullName")}</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder={t("yourFullName")}
                  required
                  className="h-12"
                  data-testid="register-fullname"
                />
              </div>
              
              <div>
                <Label htmlFor="username" className="mb-2 block">{t("username")}</Label>
                <Input
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder={t("chooseUsername")}
                  required
                  className="h-12"
                  data-testid="register-username"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="mb-2 block">{t("email")}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("yourEmail")}
                    className="h-12"
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="mb-2 block">{t("phone")}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+218 xxx"
                    className="h-12"
                    data-testid="register-phone"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="password" className="mb-2 block">{t("password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t("createPassword")}
                    required
                    className={`h-12 ${isRTL ? 'pl-12' : 'pr-12'}`}
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground`}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword" className="mb-2 block">{t("confirmPassword")}</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={t("confirmYourPassword")}
                  required
                  className="h-12"
                  data-testid="register-confirm-password"
                />
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#1a365d] hover:bg-[#1a365d]/90 h-12 mt-6"
                data-testid="register-submit"
              >
                {isLoading ? t("creatingAccount") : t("createAccount")}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-muted-foreground">
              {t("alreadyHaveAccount")}{" "}
              <Link to="/login" className="text-[#1a365d] hover:underline font-medium">
                {t("signIn")}
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
